import React, { useState } from "react";
import { Sparkles, Plus, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";

const sevTone = (lvl) => ({
  Low: "text-emerald-300 border-emerald-500/40",
  Medium: "text-amber-300 border-amber-500/40",
  High: "text-rose-300 border-rose-500/40",
}[lvl] || "text-slate-300 border-slate-700");

export default function AISuggestRisksButton({ projectId, locked, onLocked, onAdded }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [adding, setAdding] = useState({});

  const close = () => setOpen(false);

  const generate = async () => {
    if (locked) { onLocked && onLocked(); return; }
    setOpen(true); setLoading(true); setSuggestions([]);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks/suggest`);
      setSuggestions(data.suggestions || []);
      if ((data.suggestions || []).length === 0) {
        toast.message("The AI couldn't find a new risk right now — try again after more actuals land.");
      }
    } catch (e) { toast.error(errMsg(e)); setOpen(false); }
    setLoading(false);
  };

  const addOne = async (idx) => {
    const s = suggestions[idx];
    if (!s) return;
    setAdding((m) => ({ ...m, [idx]: true }));
    try {
      await api.post(`/projects/${projectId}/risks`, {
        data: {
          description: s.description,
          likelihood: s.likelihood,
          impact: s.impact,
          mitigation: s.mitigation,
          task_number: s.task_number,
          status: "Open",
        },
      });
      setSuggestions((xs) => xs.filter((_, i) => i !== idx));
      toast.success("Added to risk register");
      onAdded && onAdded();
    } catch (e) { toast.error(errMsg(e)); }
    setAdding((m) => ({ ...m, [idx]: false }));
  };

  const addAll = async () => {
    for (let i = suggestions.length - 1; i >= 0; i--) {
      // sequential to preserve audit order and avoid duplicate task-number spam
      // eslint-disable-next-line no-await-in-loop
      await addOne(i);
    }
    close();
  };

  return (
    <>
      <button
        onClick={generate}
        disabled={locked}
        data-testid="ai-suggest-risks-btn"
        className={`inline-flex items-center gap-2 border border-blue-500/40 bg-blue-500/10 text-blue-200 px-3 py-2 rounded-lg text-sm font-medium ${locked ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-500/20"}`}
      >
        <Sparkles size={14} /> AI suggest risks
      </button>
      <Modal open={open} onClose={close} size="2xl" testId="ai-suggest-modal">
        <ModalHeader icon={Sparkles} title="AI-suggested risks" onClose={close} testId="close-ai-suggest-modal" />
        <p className="text-xs text-slate-400 mb-4">Grounded in your current slippage, dependencies, critical path, and resource load. Pick the ones that fit — they land straight in your register as <em>Open</em>.</p>
        {loading && (
          <div className="flex items-center gap-2 text-slate-300 py-6" data-testid="ai-suggest-loading">
            <Loader2 className="animate-spin" size={16}/> Analysing project signals…
          </div>
        )}
        {!loading && suggestions.length === 0 && (
          <div className="py-6 text-center text-slate-400 text-sm" data-testid="ai-suggest-empty">
            No new risks to suggest right now. Add actuals or dependencies and try again.
          </div>
        )}
        {!loading && suggestions.map((s, i) => (
          <div key={`${s.description}-${s.likelihood}-${s.impact}`} className="border border-slate-800 rounded-lg p-3 mb-2" data-testid={`ai-suggestion-${i}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${sevTone(s.likelihood)}`}>L: {s.likelihood}</span>
              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${sevTone(s.impact)}`}>I: {s.impact}</span>
              {s.task_number != null && <span className="text-[10px] text-slate-400">#{s.task_number}</span>}
            </div>
            <div className="text-sm text-white font-medium leading-snug">{s.description}</div>
            {s.mitigation && <div className="text-xs text-slate-400 mt-1">↳ {s.mitigation}</div>}
            <div className="mt-2 flex justify-end">
              <button onClick={() => addOne(i)} disabled={!!adding[i]} data-testid={`ai-add-suggestion-${i}`}
                className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-2.5 py-1 rounded text-xs disabled:opacity-50">
                {adding[i] ? <Loader2 className="animate-spin" size={12}/> : <Plus size={12}/>} Add to register
              </button>
            </div>
          </div>
        ))}
        {!loading && suggestions.length > 1 && (
          <div className="mt-3 flex items-center justify-end gap-2">
            <button onClick={generate} data-testid="ai-regen-btn" className="text-sm text-slate-400 hover:text-white px-3 py-2">Regenerate</button>
            <button onClick={addAll} data-testid="ai-add-all-btn" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-3 py-2 rounded-lg text-sm">Add all {suggestions.length}</button>
          </div>
        )}
      </Modal>
    </>
  );
}
